import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { admiDataCollectionService } from '../admi-data-collection.service';
import { admiRealEstateAssetService } from '../admi-real-estate-asset.service';
import { RealEstateAssetHistory } from '../real-estate-asset-management.dto';
import { formatHistoryEntry } from './history.helpers';

interface HistoryTabProps {
    assetId: string;
}

const RECENT_CHANGES_LIMIT = 10;

/**
 * Renders the "History" tab on the Real Estate Asset Management page. Shows
 * the create/last-changed summary and the last 10 audit entries spanning the
 * REA itself plus every entity currently linked to it (buildings, service
 * mappings, future: meters). Field labels are resolved at render time via
 * `realEstateAsset.history.fieldLabel.<id>` — see `history.helpers.ts`.
 */
export function HistoryTab({ assetId }: HistoryTabProps) {
    const { t, i18n } = useTranslation();
    const [history, setHistory] = useState<RealEstateAssetHistory | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [requirementFieldLabels, setRequirementFieldLabels] = useState<Record<string, string>>({});

    // COLLECTED_ITEM entries carry the requirement-field code as field_id; the
    // configured label lives in the requirement catalog. Purely cosmetic — on
    // fetch failure the entries fall back to humanized codes, so no error state.
    useEffect(() => {
        let cancelled = false;
        admiDataCollectionService
            .getRequirementGroupsCatalog()
            .then((groups) => {
                if (cancelled) return;
                const labels: Record<string, string> = {};
                groups?.forEach((group) =>
                    group.requirementFields?.forEach((field) => {
                        labels[field.code] = field.label;
                    }),
                );
                setRequirementFieldLabels(labels);
            })
            .catch(() => {
                /* fall back to humanized codes */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        admiRealEstateAssetService
            .getRealEstateAssetHistory(assetId, RECENT_CHANGES_LIMIT)
            .then((data) => {
                if (!cancelled) setHistory(data);
            })
            .catch(() => {
                if (!cancelled) setError(t('Failed to load history.'));
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [assetId, t]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!history) {
        return <Alert severity="info">{t('No history available.')}</Alert>;
    }

    return (
        <Stack spacing={2}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    columnGap: 2,
                    rowGap: 1,
                    alignItems: 'baseline',
                }}
            >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('REA created at')}
                </Typography>
                <Typography variant="body2">
                    {history.createdAt ? (
                        new Date(history.createdAt).toLocaleString(i18n.language)
                    ) : (
                        <span style={{ color: '#aaa' }}>–</span>
                    )}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('REA created by')}
                </Typography>
                <Typography variant="body2">
                    {history.createdBy?.name ?? <span style={{ color: '#aaa' }}>–</span>}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('Last change made at')}
                </Typography>
                <Typography variant="body2">
                    {history.lastChangedAt ? (
                        new Date(history.lastChangedAt).toLocaleString(i18n.language)
                    ) : (
                        <span style={{ color: '#aaa' }}>–</span>
                    )}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('Last change made by')}
                </Typography>
                <Typography variant="body2">
                    {history.lastChangedBy?.name ?? <span style={{ color: '#aaa' }}>–</span>}
                </Typography>
            </Box>

            <Divider />

            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    {t('Recent changes')}
                </Typography>
                {history.recentChanges.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('No changes have been recorded yet.')}
                    </Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {history.recentChanges.map((entry) => {
                            const formatted = formatHistoryEntry(entry, t, i18n.language, requirementFieldLabels);
                            // entityId+occurredAt+fieldId uniquely identify a row since the
                            // backend writes one per (field, occurredAt, entity) combo.
                            const key = `${entry.entityId}:${entry.occurredAt}:${entry.fieldId ?? entry.changeType}`;
                            return (
                                <Box
                                    key={key}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 1,
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {formatted.occurredAt}
                                    </Typography>
                                    <Typography variant="body2">{formatted.headline}</Typography>
                                    {formatted.showValues && (
                                        <Box sx={{ mt: 0.5, pl: 1 }}>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {t('Old value')}: <em>{formatted.oldValue}</em>
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {t('New value')}: <em>{formatted.newValue}</em>
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </Stack>
    );
}
